import React, { useEffect, useState } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { Document, DocumentFile, MachineDocumentState } from "../../models/machineDocumentsModel";
import { deleteDocument, getDocuments, selectDocument } from "../../customerMachineDocumentsSlice";
import { useConfirmation } from "../../../../shared/components/confirmation/ConfirmationProvider";
import FullHeight from "../../../../shared/components/FullHeight";
import Loader from "../../../../shared/components/Loader";
import colors from "../../../../shared/themes/colors";
import { Box, Button, MenuItem } from "@mui/material";
import Moment from "react-moment";
import { formatFileSize } from "../../../../shared/models/models";
import TableActionButton from "../../../../shared/components/tables/TableActionButton";
import DownloadIcon from "../../../../shared/assets/icons/DownloadIcon";
import CreateDocumentDialog from "./CreateDocumentDialog";
import ChangeDocumentDialog from "./ChangeDocumentDialog";
import OverflowTooltip from "../../../../shared/components/OverflowTooltip";
import useTranslation from "../../../../shared/hooks/useTranslation";
import PermissionGuard from "../../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../../core/models/PermissionModels";

interface DocumentProps {
  documentFile?: Document;
  documentIndex: number;
  onOpenChange?: () => void;
  machineId: string;
}

const DocumentItem = ({ documentFile, onOpenChange, machineId, documentIndex }: DocumentProps) => {
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const { translate: tCommon } = useTranslation("common");
  const { translate } = useTranslation("myMachinery");

  const handleEditDocument = (item: Document) => {
    dispatch(selectDocument(item));
    onOpenChange?.();
  };

  const handleDeleteDocument = async (item: Document) => {
    if (item) {
      await dispatch(deleteDocument(item.id));
      await dispatch(getDocuments(machineId));
    }
  };

  const handleDownload = (file: DocumentFile | null) => {
    if (!file) {
      return;
    }

    const link = document.createElement("a");
    link.href = file.content;
    link.target = "_blank";
    link.click();
    link.remove();
  };

  return (
    <>
      {documentFile && (
        <Box
          className="flex justify-between items-center w-full px-4 py-3"
          sx={{ background: colors.secondary.gray98, borderRadius: "4px" }}
        >
          <Box className="flex gap-6">
            <Box className="text-14-medium" color="#333333">
              {documentFile.name}
            </Box>
            <Box className="text-14-regular" color="#333333">
              <Moment date={documentFile.createdDate} format="DD.MM.YYYY, HH:mm" />
            </Box>
            {documentFile.file && (
              <Box className="flex gap-6">
                <Box sx={{ maxWidth: "300px" }}>
                  <OverflowTooltip text={documentFile.file.name} className="text-14-regular" color={colors.secondary.gray40} />
                </Box>
                <Box className="text-14-regular" color="#7A8093">
                  {formatFileSize(documentFile.file.size)}
                </Box>
              </Box>
            )}
          </Box>

          <TableActionButton>
            {documentFile?.file && (
              <MenuItem onClick={() => handleDownload(documentFile.file)}>
                <DownloadIcon width={18} height={18} />
                {tCommon("buttons.download")}
              </MenuItem>
            )}

            <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
              <MenuItem onClick={() => handleEditDocument(documentFile)}>
                <EditOutlinedIcon />
                {tCommon("buttons.edit")}
              </MenuItem>
            </PermissionGuard>

            <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Admin}>
              <MenuItem
                onClick={() =>
                  confirm({
                    title: translate("deleteDocumentTitle"),
                    text: translate("deleteDocumentText", { name: documentFile.name }),
                    confirmButton: translate("yesDelete"),
                    closeButton: tCommon("common.no"),
                    severity: "error",
                    onConfirm: () => handleDeleteDocument(documentFile)
                  })
                }
              >
                <DeleteOutlinedIcon />
                {tCommon("buttons.delete")}
              </MenuItem>
            </PermissionGuard>
          </TableActionButton>
        </Box>
      )}
    </>
  );
};

interface ManualsAndSoftwareProps {
  machineId: string;
}

const ManualsAndSoftware = ({ machineId }: ManualsAndSoftwareProps) => {
  const { documents, loading, success, error } = useAppSelector<MachineDocumentState>(
    (state: any) => state.customerMachineDocuments
  );

  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");

  const [isCreateDocumentDialog, setIsCreateDocumentDialog] = useState(false);
  const [isChangeDocumentDialog, setIsChangeDocumentDialog] = useState(false);

  const handleCreateDocument = () => {
    dispatch(selectDocument(null));
    setIsCreateDocumentDialog(true);
  };

  const downloadFiles = async () => {
    if (!documents) {
      return;
    }

    const urls = documents.map((x) => x.file?.content);

    for (let url of urls) {
      if (url) {
        let a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", "");
        a.click();
        a.remove();
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
  };

  const handleDownloadFiles = () => {
    downloadFiles();
  };

  useEffect(() => {
    dispatch(getDocuments(machineId));
  }, []);

  return (
    <Box className="px-4 h-full">
      {loading ? (
        <FullHeight verticalOffset="medium">
          <Loader />
        </FullHeight>
      ) : !documents?.length ? (
        <FullHeight>
          <Box
            className="text-18-regular"
            color={colors.secondary.gray40}
            sx={{ display: "flex", flexDirection: "column", mt: "100px", alignItems: "center" }}
          >
            <Box>{translate("manualsAndSoftwareEmpty")}</Box>
            <Box className="mt-2">
              <Button variant="contained" onClick={handleCreateDocument}>
                {translate("addItem")}
              </Button>
            </Box>
          </Box>
        </FullHeight>
      ) : (
        <Box className="flex flex-col gap-5 h-full">
          {documents?.map((document, i) => (
            <DocumentItem
              key={i}
              documentFile={document}
              documentIndex={i}
              machineId={machineId}
              onOpenChange={() => setIsChangeDocumentDialog(true)}
            />
          ))}

          {documents && documents?.length > 0 && (
            <Box className="flex justify-end gap-2 mt-2">
              <Button variant="outlined" onClick={handleDownloadFiles}>
                {translate("downloadAllFiles")}
              </Button>
              <Button variant="contained" onClick={handleCreateDocument}>
                {translate("addItem")}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {isCreateDocumentDialog && <CreateDocumentDialog
        open={isCreateDocumentDialog}
        onClose={() => setIsCreateDocumentDialog(false)}
        machineId={machineId}
      />}

      <ChangeDocumentDialog
        open={isChangeDocumentDialog}
        onClose={() => setIsChangeDocumentDialog(false)}
        machineId={machineId}
      />
    </Box>
  );
};
export default ManualsAndSoftware;